import { useState } from 'react';
import { Navigate } from 'react-router-dom';

import { AccountRequestsPanel } from '@/components/admin/AccountRequestsPanel';
import { CreateOrgPanel } from '@/components/admin/CreateOrgPanel';
import { CreateUserPanel } from '@/components/admin/CreateUserPanel';
import { EditOrgModal } from '@/components/admin/EditOrgModal';
import { EditUserModal } from '@/components/admin/EditUserModal';
import { SettingsPanel } from '@/components/admin/SettingsPanel';
import { SystemDistributorSettingsPanel } from '@/components/admin/SystemDistributorSettingsPanel';
import { SystemSmtpSettingsPanel } from '@/components/admin/SystemSmtpSettingsPanel';
import { Button } from '@/components/ui/Button';
import {
  type AdminOrg,
  type AdminUser,
  useAdminOrgs,
  useAdminUsers,
  useToggleUserActive,
} from '@/lib/admin';
import { useMe } from '@/lib/auth';
import { cn } from '@/lib/cn';

export function AdminPage() {
  const me = useMe();
  const isAdmin =
    me.data?.user.is_superuser || me.data?.system_roles.includes('admin');

  const users = useAdminUsers();
  const orgs = useAdminOrgs();
  const toggle = useToggleUserActive();
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [editingOrgSlug, setEditingOrgSlug] = useState<string | null>(null);

  if (me.data && !isAdmin) return <Navigate to="/" replace />;

  // Re-resolve from the live query data so the modal sees the latest
  // state after a save (the lib hooks invalidate the list).
  const editingUser =
    editingUserId !== null
      ? users.data?.find((u) => u.id === editingUserId) ?? null
      : null;
  const editingOrg =
    editingOrgSlug !== null
      ? orgs.data?.find((o) => o.slug === editingOrgSlug) ?? null
      : null;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Admin</h1>
        <p className="mt-1 text-stone-600 dark:text-stone-400">
          System-wide user and organization management.
        </p>
      </div>

      <SettingsPanel />

      <AccountRequestsPanel scope={{ kind: 'site' }} />

      <SystemDistributorSettingsPanel />

      <SystemSmtpSettingsPanel />

      <div className="grid gap-6 lg:grid-cols-2">
        <CreateUserPanel />
        <CreateOrgPanel />
      </div>

      <section>
        <h2 className="mb-2 text-lg font-semibold">Users</h2>
        <div className="overflow-hidden rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900">
          <table className="w-full text-left text-sm">
            <thead className="bg-stone-50 dark:bg-stone-900 text-xs uppercase text-stone-500 dark:text-stone-400">
              <tr>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Active</th>
                <th className="px-3 py-2">Roles</th>
                <th className="px-3 py-2">Organizations</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {users.isPending && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-stone-400 dark:text-stone-500">
                    Loading…
                  </td>
                </tr>
              )}
              {users.data?.map((u) => (
                <UserRow
                  key={u.id}
                  user={u}
                  isSelf={u.id === me.data?.user.id}
                  onToggle={() =>
                    toggle.mutate({ userId: u.id, active: !u.is_active })
                  }
                  onEdit={() => setEditingUserId(u.id)}
                  busy={toggle.isPending && toggle.variables?.userId === u.id}
                />
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-lg font-semibold">Organizations</h2>
        <div className="overflow-hidden rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900">
          <table className="w-full text-left text-sm">
            <thead className="bg-stone-50 dark:bg-stone-900 text-xs uppercase text-stone-500 dark:text-stone-400">
              <tr>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Slug</th>
                <th className="px-3 py-2 text-right">Members</th>
                <th className="px-3 py-2 text-right">Parts</th>
                <th className="px-3 py-2 text-right">BOMs</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {orgs.isPending && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-stone-400 dark:text-stone-500">
                    Loading…
                  </td>
                </tr>
              )}
              {orgs.data?.map((o) => (
                <OrgRow
                  key={o.id}
                  org={o}
                  onEdit={() => setEditingOrgSlug(o.slug)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {editingUser && (
        <EditUserModal
          user={editingUser}
          isSelf={editingUser.id === me.data?.user.id}
          onClose={() => setEditingUserId(null)}
        />
      )}
      {editingOrg && (
        <EditOrgModal
          org={editingOrg}
          onClose={() => setEditingOrgSlug(null)}
        />
      )}
    </div>
  );
}

function UserRow({
  user,
  isSelf,
  onToggle,
  onEdit,
  busy,
}: {
  user: AdminUser;
  isSelf: boolean;
  onToggle: () => void;
  onEdit: () => void;
  busy: boolean;
}) {
  return (
    <tr className={cn('border-t border-stone-100 dark:border-stone-800', !user.is_active && 'bg-stone-50 dark:bg-stone-900')}>
      <td className="px-3 py-2">{user.email}</td>
      <td className="px-3 py-2 text-stone-600 dark:text-stone-400">{user.name ?? '—'}</td>
      <td className="px-3 py-2">
        <span
          className={cn(
            'inline-flex rounded px-2 py-0.5 text-xs font-medium',
            user.is_active
              ? 'bg-green-100 text-green-800'
              : 'bg-stone-200 dark:bg-stone-700 text-stone-600 dark:text-stone-400',
          )}
        >
          {user.is_active ? 'active' : 'disabled'}
        </span>
      </td>
      <td className="px-3 py-2 text-xs text-stone-600 dark:text-stone-400">
        {user.is_superuser && (
          <span className="mr-1 rounded bg-forge-100 dark:bg-forge-900 px-1.5 py-0.5 text-forge-700 dark:text-forge-400">
            superuser
          </span>
        )}
        {user.system_roles.map((r) => (
          <span key={r} className="mr-1 rounded bg-stone-100 dark:bg-stone-800 px-1.5 py-0.5">
            {r}
          </span>
        ))}
      </td>
      <td className="px-3 py-2 text-xs text-stone-600 dark:text-stone-400">
        {user.organizations
          .map((m) => `${m.organization_name} (${m.role})`)
          .join(', ')}
      </td>
      <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
        <Button variant="secondary" onClick={onEdit}>
          Edit
        </Button>
        {!isSelf && (
          <Button
            variant={user.is_active ? 'ghost' : 'primary'}
            onClick={onToggle}
            disabled={busy}
          >
            {busy ? '…' : user.is_active ? 'Disable' : 'Enable'}
          </Button>
        )}
      </td>
    </tr>
  );
}

function OrgRow({ org, onEdit }: { org: AdminOrg; onEdit: () => void }) {
  return (
    <tr className="border-t border-stone-100 dark:border-stone-800">
      <td className="px-3 py-2 font-medium">{org.name}</td>
      <td className="px-3 py-2 font-mono text-xs">{org.slug}</td>
      <td className="px-3 py-2 text-right font-mono">{org.member_count}</td>
      <td className="px-3 py-2 text-right font-mono">{org.part_count}</td>
      <td className="px-3 py-2 text-right font-mono">{org.bom_count}</td>
      <td className="px-3 py-2 text-right">
        <Button variant="secondary" onClick={onEdit}>
          Edit
        </Button>
      </td>
    </tr>
  );
}
